/**
 * Kanban de leads (F3-I). Columnas = etapas del pipeline; tarjetas arrastrables
 * (drag & drop nativo) que hacen PATCH de la etapa al soltar. Filtros por origen
 * y búsqueda, acción rápida de WhatsApp y diálogo de "nuevo lead". Cada tarjeta
 * enlaza al detalle del lead.
 */
import { createSignal, For, Show } from "solid-js";
import { Link } from "@tanstack/solid-router";
import { msg } from "@lingui/core/macro";
import { MessageCircle, Plus, Search } from "lucide-solid";
import { buildWaLink } from "@space/shared";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { ServerDataGate } from "@/components/ui/server-data-gate";
import { Select } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { showToast } from "@/components/ui/toast";
import { ExportButton } from "@/features/io/components/export-button";
import { useActiveOrg } from "@/features/academy/hooks/use-active-org";
import { type Lead, SOURCES, type Stage, STAGES, useLeads, usePatchLead } from "./api";
import { SOURCE_LABEL, STAGE_LABEL } from "./labels";
import { NewLeadDialog } from "./new-lead-dialog";

const field =
  "rounded-lg border-2 border-border bg-field-background px-3 py-2 text-sm focus-visible:border-primary focus-visible:outline-none";

// El `to` tipado de TanStack es una unión estricta; un valor `string` (como el de
// la NAV del shell) se acepta y evita acoplar los enlaces a la unión generada.
const leadHref = (id: string): string => `/crm/leads/${id}`;

export function CrmKanban() {
  const { t } = useI18n();
  const { activeOrgId } = useActiveOrg();
  const [source, setSource] = createSignal<string>("");
  const [search, setSearch] = createSignal("");
  const [dialogOpen, setDialogOpen] = createSignal(false);
  const [dragging, setDragging] = createSignal<string | null>(null);

  const leads = useLeads(activeOrgId, () => ({
    source: source() || undefined,
    search: search().trim() || undefined,
  }));
  const patch = usePatchLead();

  const leadsByStage = (stage: Stage): Lead[] =>
    (leads.data ?? []).filter((l) => l.stage === stage);

  const moveTo = (leadId: string, stage: Stage) => {
    const lead = (leads.data ?? []).find((l) => l.id === leadId);
    if (!lead || lead.stage === stage) {
      return;
    }
    patch.mutate(
      { id: leadId, patch: { stage } },
      { onError: (err) => showToast(err.message, "error") },
    );
  };

  const waLink = (lead: Lead): string | null => {
    const { name } = lead;
    return buildWaLink(lead.phone, t(msg`Hi ${name}, thanks for your interest in Space Academy.`));
  };

  return (
    <div>
      <div class="mb-4 flex flex-wrap items-center gap-2">
        <div class="relative">
          <Search class="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            class={`${field} pl-8`}
            value={search()}
            onInput={(e) => setSearch(e.currentTarget.value)}
            placeholder={t(msg`Search name, phone or email`)}
            data-testid="crm-search"
          />
        </div>
        <div class="w-48">
          <Select
            options={[
              { value: "", label: t(msg`All sources`) },
              ...SOURCES.map((s) => ({ value: s, label: t(SOURCE_LABEL[s]) })),
            ]}
            value={source()}
            onChange={setSource}
            aria-label={t(msg`Source`)}
          />
        </div>
        <div class="ml-auto flex items-center gap-2">
          {/* F4-B: export de leads (io.leads) con el filtro de fuente activo. */}
          <ExportButton
            entityKey="io.leads"
            orgId={activeOrgId()}
            filters={{ fuente: source() || undefined }}
          />
          <Button onClick={() => setDialogOpen(true)} data-testid="crm-new-lead" class="gap-2">
            <Plus class="h-4 w-4" />
            {t(msg`New lead`)}
          </Button>
        </div>
      </div>

      {/* LF-B: el CRM es server-only — offline muestra estado honesto, no
          un skeleton eterno (query pending+paused sin red). */}
      <ServerDataGate
        pending={leads.isLoading}
        error={leads.isError}
        refetch={() => void leads.refetch()}
        skeleton={<Skeleton class="h-64 w-full rounded-xl" />}
      >
        <div class="flex gap-3 overflow-x-auto pb-3" data-testid="crm-kanban">
          <For each={STAGES}>
            {(stage) => (
              <div
                class="flex w-72 shrink-0 flex-col rounded-xl border border-border bg-muted/30 p-2"
                data-testid={`crm-column-${stage}`}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const id = dragging();
                  if (id) {
                    moveTo(id, stage);
                  }
                  setDragging(null);
                }}
              >
                <div class="mb-2 flex items-center justify-between px-1.5 py-1">
                  <span class="text-sm font-bold">{t(STAGE_LABEL[stage])}</span>
                  <span class="rounded-full bg-accent/15 px-2 text-xs font-semibold text-accent">
                    {leadsByStage(stage).length}
                  </span>
                </div>
                <div class="flex flex-col gap-2">
                  <For each={leadsByStage(stage)}>
                    {(lead) => (
                      <div
                        draggable="true"
                        onDragStart={() => setDragging(lead.id)}
                        onDragEnd={() => setDragging(null)}
                        class="group rounded-lg border border-border bg-card p-3 shadow-sm transition-shadow hover:shadow-md"
                        data-testid="crm-card"
                      >
                        <Link to={leadHref(lead.id)} class="block">
                          <div class="font-semibold text-foreground">{lead.name}</div>
                          <div class="text-xs text-muted-foreground">{lead.phone}</div>
                          <Show when={lead.interest}>
                            <div class="mt-1 text-xs text-muted-foreground">{lead.interest}</div>
                          </Show>
                        </Link>
                        <div class="mt-2 flex items-center justify-between">
                          <span class="rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase text-muted-foreground">
                            {t(SOURCE_LABEL[lead.source])}
                          </span>
                          <Show when={waLink(lead)}>
                            {(href) => (
                              <a
                                href={href()}
                                target="_blank"
                                rel="noreferrer"
                                class="flex items-center gap-1 text-xs font-medium text-green-600 hover:underline"
                                data-testid="crm-card-wa"
                                aria-label={t(msg`Message on WhatsApp`)}
                              >
                                <MessageCircle class="h-3.5 w-3.5" />
                                {t(msg`Message`)}
                              </a>
                            )}
                          </Show>
                        </div>
                      </div>
                    )}
                  </For>
                </div>
              </div>
            )}
          </For>
        </div>
      </ServerDataGate>

      <NewLeadDialog open={dialogOpen()} onOpenChange={setDialogOpen} org={activeOrgId()} />
    </div>
  );
}
